import React, { useEffect, useState } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import {
  createCheckoutSubscription,
  getSubscriptionPlans,
  STRIPE_PUBLISHABLE_KEY,
  validateCoupon,
} from '../../api/axios';
import Alert from '../common/Alert';
import PlanOption from './PlanOption';
import './subscriptionPlans.css';

const SubscriptionPlans = () => {
  const token = localStorage.getItem('token');
  const [selectedPlan, setSelectedPlan] = useState(0);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [alertSeverity, setAlertSeverity] = useState('error');
  const [alertMessage, setAlertMessage] = useState('');
  const [plansData, setPlansData] = useState([]);
  const [couponCode, setCouponCode] = useState('');
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [couponLoading, setCouponLoading] = useState(false);

  const showNotification = (message, severity = 'error') => {
    setAlertSeverity(severity);
    setAlertMessage(message);
    setOpenSnackbar(true);
  };

  const getSubscriptionPlansHandler = async () => {
    try {
      const result = await getSubscriptionPlans();
      if (result.status) {
        setPlansData(result.result);
        if (result.result.length > 0) {
          setSelectedPlan(result.result[0].id);
        }
      }
    } catch (error) {
      showNotification('Unable to load subscription plans.');
    }
  };

  useEffect(() => {
    getSubscriptionPlansHandler();
  }, []);

  const handlePlanSelect = (planId) => {
    setSelectedPlan(planId);
    setAppliedCoupon(null);
  };

  const selectedPlanData = plansData.find((plan) => plan.id === selectedPlan);
  const orderAmount = Number(selectedPlanData?.price || 0);
  const discountAmount = Number(appliedCoupon?.discountAmount || 0);
  const finalAmount = Math.max(0, orderAmount - discountAmount);
  const appliedCouponLabel = appliedCoupon
    ? appliedCoupon.discountType === 'fixed'
      ? `$${Number(appliedCoupon.discountValue || 0).toFixed(2)} off`
      : `${Number(
          appliedCoupon.discountValue || appliedCoupon.discountPercentage || 0
        ).toFixed(0)}% off`
    : '';

  const handleApplyCoupon = async () => {
    try {
      if (!token) {
        showNotification('Please sign in before applying a coupon.');
        return;
      }

      if (!selectedPlan) {
        showNotification('Please select a plan first.');
        return;
      }

      if (!couponCode.trim()) {
        showNotification('Please enter a coupon code.');
        return;
      }

      setCouponLoading(true);
      const response = await validateCoupon({
        code: couponCode,
        packageId: selectedPlan,
      });

      if (!response.status) {
        showNotification(response.message || 'Unable to apply coupon.');
        return;
      }

      setAppliedCoupon(response.result);
      setCouponCode(response.result.code);
      showNotification('Coupon applied successfully.', 'success');
    } catch (error) {
      setAppliedCoupon(null);
      showNotification(
        error.response?.data?.message ||
          error.message ||
          'Unable to apply coupon.'
      );
    } finally {
      setCouponLoading(false);
    }
  };

  const handleRemoveCoupon = () => {
    setAppliedCoupon(null);
    setCouponCode('');
  };

  const handleSubscribe = async () => {
    try {
      if (!token) {
        showNotification('Please sign in before subscribing.');
        return;
      }

      if (!selectedPlan) {
        showNotification('Please select a plan first.');
        return;
      }

      if (!STRIPE_PUBLISHABLE_KEY) {
        showNotification('Stripe configuration is missing. Please contact support.');
        return;
      }

      const stripe = await loadStripe(STRIPE_PUBLISHABLE_KEY);

      const response = await createCheckoutSubscription({
        packageId: selectedPlan,
        couponCode: appliedCoupon?.code || undefined,
      });

      if (!response.status) {
        showNotification('Unable to start checkout. Please try again.');
        return;
      }

      if (response.url) {
        window.location.assign(response.url);
        return;
      }

      const result = await stripe.redirectToCheckout({
        sessionId: response.id,
      });

      if (result?.error?.message) {
        showNotification(result.error.message);
      }
    } catch (error) {
      showNotification(
        error.response?.data?.error ||
          error.message ||
          'Unable to start checkout. Please try again.'
      );
    }
  };

  return (
    <div className='subscriptions-container'>
      <div className='subscription-header'>
        <h1 className='subscription-title'>Select a Plan To Subscribe</h1>
        <p className='subscription-subtitle'>
          Get Discovered by Top Industry Professionals
        </p>
      </div>
      <div className='subscription-container'>
        <div className='plans'>
          {plansData.map((plan) => (
            <PlanOption
              key={plan.id}
              plan={plan}
              isSelected={selectedPlan === plan.id}
              onSelect={() => handlePlanSelect(plan.id)}
            />
          ))}
        </div>
        <div className='coupon-card'>
          <div className='coupon-row'>
            <input
              className='coupon-input'
              type='text'
              placeholder='Enter coupon code'
              value={couponCode}
              onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
            />
            <button
              className='coupon-apply-btn'
              type='button'
              onClick={handleApplyCoupon}
              disabled={couponLoading}
            >
              {couponLoading ? 'Applying...' : 'Apply'}
            </button>
            {appliedCoupon && (
              <button
                className='coupon-remove-btn'
                type='button'
                onClick={handleRemoveCoupon}
              >
                Remove
              </button>
            )}
          </div>
          <div className='summary-lines'>
            <div className='summary-line'>
              <span>Plan price</span>
              <strong>${orderAmount.toFixed(2)}</strong>
            </div>
            <div className='summary-line'>
              <span>Discount</span>
              <strong>
                -$
                {discountAmount.toFixed(2)}
                {appliedCoupon ? ` (${appliedCouponLabel})` : ''}
              </strong>
            </div>
            <div className='summary-line total-line'>
              <span>Total</span>
              <strong>${finalAmount.toFixed(2)}</strong>
            </div>
          </div>
          {appliedCoupon && (
            <p className='coupon-applied-text'>
              Coupon <strong>{appliedCoupon.code}</strong> applied successfully.
            </p>
          )}
        </div>
        <button className='subscribe-btn' onClick={handleSubscribe}>
          Subscribe Now
        </button>
      </div>
      <Alert
        openSnackbar={openSnackbar}
        alertSeverity={alertSeverity}
        setOpenSnackbar={setOpenSnackbar}
        alertMessage={alertMessage}
      />
    </div>
  );
};

export default SubscriptionPlans;
